// Library Methods
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Styles
import { useTheme } from "@mui/material/styles";
import {
  ModalCustomConfirm,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import TodayExams, {
  TodayExamsHandleType,
} from "src/components/containers/Dashboard/TodayExams/TodayExams";
import ExpandableSearchbar from "src/components/shared/inputs/ExpandableSearchbar/ExpandableSearchbar";
import { ChevronDownIcon, PlusIcon } from "src/assets/icons";

// Hooks
import useDebounce from "src/hooks/input/useDebounce";

type TodayAccordionType = {
  timeRange: number;
  onLoadingChange?: (isLoading: boolean) => void;
};

// Mirrors RecentlyCompletedAccordion's styling exactly -- same border/
// radius/background treatment -- just wrapping TodayExams instead.
const TodayAccordion = ({ timeRange, onLoadingChange }: TodayAccordionType) => {
  const { t } = useTranslation();

  const theme = useTheme();
  const { dividerBorder } = getSurfaceTokens(theme);

  // "Add Exam" lives here now (like the searchbar/TimeRangeSwitcher bar on
  // RecentlyCompletedAccordion), but the modal/slider it opens is still
  // owned by TodayExams -- trigger it through an imperative handle rather
  // than lifting all of that state up.
  const todayExamsRef = useRef<TodayExamsHandleType>(null);
  const [isLoadingTodayExams, setIsLoadingTodayExams] = useState(true);

  useEffect(() => {
    onLoadingChange?.(isLoadingTodayExams);
  }, [isLoadingTodayExams, onLoadingChange]);

  // Reset the sub-accordions on reopening the parent accordion
  const [outerExpanded, setOuterExpanded] = useState(true);
  const [subAccordionResetKey, setSubAccordionResetKey] = useState(0);

  const handleOuterChange = (_: React.SyntheticEvent, isExpanded: boolean) => {
    setOuterExpanded(isExpanded);
    if (isExpanded) setSubAccordionResetKey((prev) => prev + 1);
  };

  // Functions the same way as the shared searchbar on RecentlyCompletedAccordion.
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm);

  const onSearchChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSearchTerm(event?.target?.value);
  };

  const clearSearchFilter = () => {
    setSearchTerm("");
  };

  return (
    <Accordion
      expanded={outerExpanded}
      onChange={handleOuterChange}
      disableGutters
      sx={{
        p: 0,
        m: 0,
        boxShadow: "none",
        backgroundColor: "transparent",
        backgroundImage: "none",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ChevronDownIcon style={{ color: "#FFF" }} />}
        sx={{
          p: 0,
          m: 0,
          px: "8px",
          minHeight: "auto",
          border: dividerBorder,
          borderRadius: "4px",
          backgroundColor: "#202338",
          "&.Mui-expanded": {
            p: 0,
            m: 0,
            px: "8px",
            minHeight: "auto",
          },
        }}
      >
        <Typography
          alignContent="center"
          fontSize="16px"
          fontWeight={600}
          textOverflow="ellipsis"
          overflow="hidden"
          whiteSpace="nowrap"
          maxWidth="100%"
          color="#FFF"
        >
          {t("Today")}
        </Typography>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          p: 0,
          m: 0,
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <Box display="flex" justifyContent="flex-end" mt="8px" gap="8px">
          <ExpandableSearchbar
            id="today-exams-searchbar"
            isLoading={false}
            searchTerm={searchTerm}
            onChange={onSearchChange}
            onClear={clearSearchFilter}
            searchTooltip={t("Search Exams")}
            openByDefault={true}
            autofocusByDefault={false}
          />
          <Button
            startIcon={<PlusIcon />}
            disabled={isLoadingTodayExams}
            onClick={() => todayExamsRef.current?.openAddExamModal()}
            sx={{
              ...ModalCustomConfirm(),
            }}
          >
            <Typography
              maxWidth="100%"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              fontSize="12px"
            >
              {t("Add Exam")}
            </Typography>
          </Button>
        </Box>
        <TodayExams
          ref={todayExamsRef}
          timeRange={timeRange}
          onLoadingChange={setIsLoadingTodayExams}
          searchTerm={debouncedSearchTerm}
          forceExpandKey={subAccordionResetKey}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default TodayAccordion;
